import { Box } from '@mui/system'
import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidateCustomer } from 'src/api/customer'
import { z } from 'zod'
import { invalidateServiceStationCache, useGetAllTypeServiceStations } from '../../../api/service-station'
import {
  invalidateServiceStationGroupsQueries,
  useCreateServiceStationGroup,
  useUpdateServiceStationGroup,
} from '../../../api/service-station-group'
import { AppForm, AppFormProps, FormSelectField, FormTextField } from '../../../components/FormCore'
import { GenericDialogLayout } from '../../../components/GenericDialogLayout'
import { MainButton } from '../../../components/MainButton'
import { IServiceStation } from '../../../generated-types/service-station'
import { useZodForm } from '../../../hooks/zod-form'
import { ShowDialogProps } from '../../../show-dialog'
import { getServiceStationType } from '../../../utils/function'
import { GroupHeader, GroupItems } from '../../localities/positions/components/MainComponentSendModal'
interface ServiceStationGroupDialogProps extends ShowDialogProps, AppFormProps {
  title: string
  customerId: string
}

const schema = z.object({
  id: z.number().optional().nullable(),
  name: z.string().nonempty(),
  description: z.string().optional().nullable(),
  serviceStations: z.array(z.object({ id: z.number() })).transform(data => data.map(({ id }) => id)),
})

export const ServiceStationGroupDialog: FC<ServiceStationGroupDialogProps> = ({
  title,
  customerId,
  onSubmit,
  initialValues,
  pop,
}) => {
  const { data } = useGetAllTypeServiceStations(customerId)
  const { mutateAsync: addServiceStationGroup } = useCreateServiceStationGroup(customerId)
  const { mutateAsync: updateServiceStationGroup } = useUpdateServiceStationGroup(customerId)

  const handleAddServiceStationGroup = async (values: any) => {
    await addServiceStationGroup({ ...values })
      .then(() => {
        invalidateServiceStationGroupsQueries.getServiceStationGroups(customerId as EntityId)
        invalidateServiceStationCache.GetWithGroups(customerId as EntityId)
        toast.success(t('service-station-group-added-successfully', { ns: 'service-station-group' }))
        pop()
      })
      .catch(error => {
        toast.error(t('service-station-group-added-error', { ns: 'service-station-group' }))
      })
  }

  const handleUpdateServiceStationGroup = async (values: any) => {
    await updateServiceStationGroup(
      { ...values },
      {
        onSuccess: () => {
          invalidateServiceStationGroupsQueries.getServiceStationGroups(customerId as EntityId)
          invalidateServiceStationCache.GetWithGroups(customerId as EntityId)
          invalidateCustomer.getCustomerUsersForAdmins(customerId)
          toast.success(t('service-station-group-updated-successfully', { ns: 'service-station-group' }))
          pop()
        },
        onError: () => {
          toast.error(t('service-station-group-updated-error', { ns: 'service-station-group' }))
        },
      },
    )
  }

  const { t } = useTranslation(['service-station-group', 'common'])
  const form = useZodForm(schema, {
    defaultValues: { ...initialValues, serviceStations: initialValues?.serviceStations ?? [] },
  })

  const serviceStations = useMemo(() => {
    const services = data?.items ?? ([] as IServiceStation[])
    services.sort((a, b) => b.type.toString().localeCompare(a.type.toString()))
    return services
  }, [data?.items])

  return (
    <GenericDialogLayout
      title={title}
      pop={pop}
      sx={{ p: 1 }}
      actions={[
        <MainButton
          key="submit"
          onClick={() => {
            form.handleSubmit(initialValues?.id ? handleUpdateServiceStationGroup : handleAddServiceStationGroup)()
          }}
        >
          {t(initialValues?.id ? 'update' : 'add', { ns: 'common' })}
        </MainButton>,
      ]}
      removedCloseButton={false}
    >
      <AppForm form={form} onSubmit={onSubmit} hasInitialValues={!!initialValues}>
        <FormTextField fullWidth name="name" label={t('name', { ns: 'common' })} />
        <FormTextField fullWidth name="description" label={t('description', { ns: 'common' })} multiline rows={4} />
        <FormSelectField
          multiple
          name="serviceStations"
          data={serviceStations}
          defaultValue={initialValues?.serviceStations || []}
          label={t('service-stations', { ns: 'service-station-group' })}
          getOptionLabel={(option: IServiceStation) => option.name}
          renderOption={(props: any, option) => (
            <Box {...props} key={option.id}>
              {option.name}
            </Box>
          )}
          groupBy={(option: any) => option.type}
          renderGroup={params => (
            <li>
              <GroupHeader>{t(getServiceStationType(params.group) ?? '', { ns: 'service-station' })}</GroupHeader>
              <GroupItems>{params.children}</GroupItems>
            </li>
          )}
          isOptionEqualToValue={(o, v) => o.id === v.id}
        />
      </AppForm>
    </GenericDialogLayout>
  )
}
